import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  FiatCurrency,
  InGameCurrency,
  Rarity,
  RewardConfigRarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { missionBackground } from '@sorare/core/src/components/missions/style';
import { RewardUnlockedAnimation } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation';
import {
  ANY_SPORT_MARKET_COMMON_PACKS,
  ANY_SPORT_MARKET_INSTANT_BUY,
  ANY_SPORT_MARKET_WHEEL,
  ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useGetMyCardsBoostLink } from '@sorare/core/src/hooks/my-cards/useGetMyCardsBoostLink';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { fantasy, glossary, wheel } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  getRewardImage,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CraftUnlockedDialog } from 'components/onboarding/CraftUnlockedDialog';
import { IntroducingRoadToPro } from 'components/onboarding/IntroducingRoadToPro';
import { RewardBoxDialogWithOnboarding } from 'components/rewards/RewardBoxDialogWithOnboarding';

import type {
  RewardUnlockedStory_rewardConfig,
  RewardUnlockedStory_taskInterface,
} from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ center: true })`
  position: relative;
  ${missionBackground}
  height: 100%;
  width: 100%;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-height: min(calc(90 * var(--unit)), 100vh);
  }
`;

const ButtonsWrapper = styled(Vertical)`
  position: absolute;
  bottom: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);

  opacity: 0;
  animation: ${fadeIn} 0.65s cubic-bezier(0.75, 0.07, 0.23, 0.89) forwards;
  animation-delay: 1.35s;
`;

const messages = defineMessages({
  defaultTitle: {
    id: 'RewardUnlockedDialog.defaultTitle',
    defaultMessage: "You've won a reward!",
  },
  xpTitle: {
    id: 'RewardUnlockedDialog.xpTitle2',
    defaultMessage: "You've gained {rarity} XP!",
  },
  mixedXpTitle: {
    id: 'RewardUnlockedDialog.mixedXpTitle3',
    defaultMessage: "You've gained XP!",
  },
  xpSubtitle: {
    id: 'RewardUnlockedDialog.xpSubtitle',
    defaultMessage: 'Level up your players with XP.',
  },
  essenceTitle: {
    id: 'RewardUnlockedDialog.essenceTitleV2',
    defaultMessage: "You've won {rarity} Essence!",
  },
  essenceSubtitle: {
    id: 'RewardUnlockedDialog.essenceSubtitle',
    defaultMessage: 'Use Essence to craft new players.',
  },
  essenceSubtitleCommon: {
    id: 'RewardUnlockedDialog.essenceSubtitleCommon',
    defaultMessage: 'Use Essence to buy new packs.',
  },
  monetaryTitle: {
    id: 'RewardUnlockedDialog.monetaryTitle',
    defaultMessage: "You've won cash!",
  },
  marketCreditTitle: {
    id: 'RewardUnlockedDialog.marketCreditTitle',
    defaultMessage: "You've won market credits!",
  },
  marketCreditSubTitle: {
    id: 'RewardUnlockedDialog.marketCreditSubTitle2',
    defaultMessage: 'Use them in Auctions or Instant Buy',
  },
  wheelTicketTitle: {
    id: 'RewardUnlockedDialog.wheelTicketTitle',
    defaultMessage:
      "You've won {rarity} {count, plural, one {wheel ticket} other {wheel tickets}}!",
  },
  wheelTicketSubtitle: {
    id: 'RewardUnlockedDialog.wheelTicketSubtitle',
    defaultMessage: 'Use them to spin the wheel.',
  },
});

type Props = {
  open: boolean;
  onClose: () => void;
  task: RewardUnlockedStory_taskInterface;
  alternativeRewardConfigs?: RewardUnlockedStory_rewardConfig[];
};

type DumbRewardUnlockedStoryProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: string;
  amount?: string;
  action?: ReactNode;
  rewardConfigs: RewardUnlockedStory_taskInterface['rewardConfigs'];
  rgbColor?: string;
};

const DumbRewardUnlockedStory = ({
  onClose,
  title = <FormattedMessage {...messages.defaultTitle} />,
  subtitle,
  amount,
  action,
  rewardConfigs,
  rgbColor = 'var(--c-rgb-green-400)',
}: DumbRewardUnlockedStoryProps) => {
  return (
    <Root
      style={
        {
          '--rgb-color': rgbColor,
        } as CSSProperties
      }
    >
      <RewardUnlockedAnimation
        imgUrls={rewardConfigs.map(r => getRewardImage(r))}
        title={title}
        subtitle={subtitle}
        amount={amount}
      />
      <ButtonsWrapper>
        {action}
        <Button
          size="large"
          onClick={() => {
            onClose?.();
          }}
          color={action ? 'transparent' : 'quaternary'}
          disableDebounce
        >
          <FormattedMessage {...glossary.continue} />
        </Button>
      </ButtonsWrapper>
    </Root>
  );
};

export const RewardUnlockedStory = ({
  open,
  onClose,
  task,
  alternativeRewardConfigs,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { sport, generateSportContextPath } = useSportContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { currentUser } = useCurrentUserContext();
  const [rewardBoxDialogOpen, setRewardBoxDialogOpen] = useState(false);
  const { formatMessage } = useIntl();
  const rewardConfigs = alternativeRewardConfigs || task.rewardConfigs;
  const {
    flags: { useRoadToPro = false },
  } = useFeatureFlags();
  const { showSimplifiedNav } = useSimplifiedNav(sport);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const [showRoadToProDialog, setShowRoadToProDialog] = useState(false);
  const [showCraftUnlockedDialog, setShowCraftUnlockedDialog] = useState(false);

  const mergedRewardConfigs = mergeRewardsConfig(rewardConfigs);
  const firstRewardConfig = mergedRewardConfigs.at(0);

  if (!firstRewardConfig) {
    return null;
  }

  const rewardsGroupedByType = groupBy(rewardConfigs, r => r.__typename);

  // We only support multiple rewards with the same type for now
  if (Object.keys(rewardsGroupedByType).length === 1) {
    // TODO: Handle other summable types
    if (rewardsGroupedByType.InGameCurrencyRewardConfig) {
      const inGameCurrenciesGroupedByCurrency = groupBy(
        rewardsGroupedByType.InGameCurrencyRewardConfig,
        r => r.currency
      );
      const rewardCountsByCurrency = Object.entries(
        inGameCurrenciesGroupedByCurrency
      ).reduce<Record<InGameCurrency, number>>(
        (acc, [currency, rewards]) => {
          acc[currency as InGameCurrency] = rewards.reduce<number>(
            (sum, curr) => {
              return sum + curr.amount;
            },
            0
          );
          return acc;
        },
        {} as Record<InGameCurrency, number>
      );
      const rarityTyped =
        inGameCurrencyToRarity[
          Object.keys(rewardCountsByCurrency)[0] as InGameCurrency
        ];
      let amount;
      let title;
      let subtitle = formatMessage(messages.xpSubtitle);
      let action = (
        <Button
          size="large"
          to={getMyCardsBoostLink({
            rarity: rarityTyped || Rarity.common,
            sport,
          })}
          color="primary"
        >
          <FormattedMessage {...fantasy.levelUp} />
        </Button>
      );
      if (Object.keys(rewardCountsByCurrency).length === 1) {
        const currency = Object.keys(rewardCountsByCurrency).at(
          0
        ) as InGameCurrency;
        if (
          currency === InGameCurrency.COMMON_WHEEL_TICKET ||
          currency === InGameCurrency.WHEEL_TICKET
        ) {
          const rarity =
            currency === InGameCurrency.COMMON_WHEEL_TICKET
              ? Rarity.common
              : Rarity.limited;
          title = formatMessage(messages.wheelTicketTitle, {
            rarity: scarcityNames[rarity],
            count: Object.values(rewardCountsByCurrency).at(0),
          });
          subtitle = formatMessage(messages.wheelTicketSubtitle);
          action = (
            <Button
              size="large"
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL)}
              color="primary"
            >
              <FormattedMessage {...wheel.spin} />
            </Button>
          );
        } else {
          title = formatMessage(messages.xpTitle, {
            rarity: scarcityNames[rarityTyped || Rarity.common],
          });
        }
        amount = formatNumber(Object.values(rewardCountsByCurrency)[0]);
      } else {
        title = formatMessage(messages.mixedXpTitle);
        amount = formatNumber(
          Object.values(rewardCountsByCurrency).reduce(
            (acc, curr) => acc + curr,
            0
          )
        );
      }
      return (
        <DumbRewardUnlockedStory
          open={open}
          onClose={onClose}
          rewardConfigs={rewardConfigs}
          title={title}
          amount={amount}
          subtitle={subtitle}
          rgbColor={
            Object.keys(rewardCountsByCurrency).length === 1 && rarityTyped
              ? getRarityRGBColor(rarityTyped)
              : undefined
          }
          action={action}
        />
      );
    }
  }

  if (isType(firstRewardConfig, 'CardShardRewardConfig')) {
    const rarity = (
      firstRewardConfig.configRarity !== RewardConfigRarity.unknown_rarity
        ? firstRewardConfig.configRarity
        : Rarity.limited
    ) as Rarity;
    const isCommonPackEssence =
      firstRewardConfig.configRarity === Rarity.common &&
      sport !== Sport.BASEBALL;
    const showRoadToProElements =
      sport === Sport.FOOTBALL &&
      useRoadToPro &&
      showSimplifiedNav &&
      rarity === Rarity.limited;
    const showRoadToProUnlocked =
      showRoadToProElements && !lifecycle?.sawRoadToProUnlocked;

    const limitedCardShardsChest = currentUser?.shardsChests?.find(
      chest => chest.rarity === Rarity.limited
    );
    const craftThreshold =
      limitedCardShardsChest?.thresholds.at(0)?.threshold || 0;
    const limitedCardShardsCount = limitedCardShardsChest?.cardShardsCount || 0;
    const canCraft = craftThreshold && limitedCardShardsCount >= craftThreshold;
    const showRoadToProCraftUnlocked =
      showRoadToProElements &&
      lifecycle?.sawRoadToProUnlocked &&
      !lifecycle?.sawRoadToProCraftUnlocked &&
      canCraft &&
      currentUser?.cardCounts.limited === 0;

    let action: ReactNode = (
      <Button
        to={generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE_TYPE, {
          params: { type: rarity.toLowerCase() },
        })}
        size="large"
        color="primary"
      >
        <FormattedMessage
          id="RewardUnlockedDialog.CardShardRewardConfig.cta"
          defaultMessage="Craft players"
        />
      </Button>
    );

    if (isCommonPackEssence) {
      action = (
        <Button
          to={generateSportContextPath(ANY_SPORT_MARKET_COMMON_PACKS)}
          size="large"
          color="primary"
        >
          <FormattedMessage
            id="RewardUnlockedDialog.CardShardRewardConfig.ctaBuyPacks"
            defaultMessage="Buy packs"
          />
        </Button>
      );
    }

    if (
      showRoadToProUnlocked ||
      showRoadToProCraftUnlocked ||
      (showSimplifiedNav && limitedCardShardsCount < craftThreshold)
    ) {
      action = undefined;
    }

    return (
      <>
        <DumbRewardUnlockedStory
          open={open}
          onClose={() => {
            if (showRoadToProUnlocked) {
              setShowRoadToProDialog(true);
            } else if (showRoadToProCraftUnlocked) {
              setShowCraftUnlockedDialog(true);
            } else {
              onClose();
            }
          }}
          rewardConfigs={rewardConfigs}
          title={formatMessage(messages.essenceTitle, {
            rarity: scarcityNames[rarity],
          })}
          subtitle={formatMessage(
            isCommonPackEssence
              ? messages.essenceSubtitleCommon
              : messages.essenceSubtitle
          )}
          amount={formatNumber(firstRewardConfig.quantity)}
          rgbColor={getRarityRGBColor(rarity)}
          action={action}
        />
        <IntroducingRoadToPro
          open={showRoadToProDialog}
          onClose={() => {
            onClose();
            setShowRoadToProDialog(false);
            updateLifecycle(LIFECYCLE.sawRoadToProUnlocked, true);
          }}
        />
        <CraftUnlockedDialog
          open={showCraftUnlockedDialog}
          onClose={() => {
            onClose();
            setShowCraftUnlockedDialog(false);
            updateLifecycle(LIFECYCLE.sawRoadToProCraftUnlocked, true);
          }}
        />
      </>
    );
  }

  if (isType(firstRewardConfig, 'ConversionCreditRewardConfig')) {
    const { maxDiscount } = firstRewardConfig;
    const usdAmount = formatNumber((maxDiscount.usdCents || 0) / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
    return (
      <DumbRewardUnlockedStory
        open={open}
        onClose={onClose}
        rewardConfigs={rewardConfigs}
        title={formatMessage(messages.marketCreditTitle)}
        subtitle={formatMessage(messages.marketCreditSubTitle)}
        amount={usdAmount}
        action={
          <Button
            size="large"
            to={generateSportContextPath(ANY_SPORT_MARKET_INSTANT_BUY)}
            color="primary"
          >
            <FormattedMessage
              id="RewardUnlockedDialog.ConversionCreditRewardConfig.cta"
              defaultMessage="Scout players"
            />
          </Button>
        }
        rgbColor="29, 60, 55"
      />
    );
  }

  if (isType(firstRewardConfig, 'MonetaryRewardConfig')) {
    const totalCash = toMonetaryAmount(firstRewardConfig.monetaryAmount).usd;

    const formattedTotalCash = formatNumber(totalCash, {
      style: 'currency',
      currency: FiatCurrency.USD,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    });

    return (
      <DumbRewardUnlockedStory
        open={open}
        onClose={onClose}
        rewardConfigs={rewardConfigs}
        title={formatMessage(messages.monetaryTitle)}
        amount={formattedTotalCash}
        rgbColor="49, 42, 99"
      />
    );
  }

  const taskBundle = task.probabilisticBundles.at(0);

  if (
    taskBundle &&
    isType(firstRewardConfig, 'ProbabilisticBundleRewardConfig')
  ) {
    return (
      <>
        <DumbRewardUnlockedStory
          open={open}
          onClose={() => {
            setRewardBoxDialogOpen(true);
          }}
          rewardConfigs={rewardConfigs}
        />
        <RewardBoxDialogWithOnboarding
          open={rewardBoxDialogOpen}
          onClose={() => {
            setRewardBoxDialogOpen(false);
            onClose();
          }}
          bundle={taskBundle}
        />
      </>
    );
  }

  return (
    <DumbRewardUnlockedStory
      open={open}
      onClose={() => {
        if (!taskBundle) {
          onClose();
        } else setRewardBoxDialogOpen(true);
      }}
      rewardConfigs={rewardConfigs}
    />
  );
};

const fragments = {
  rewardConfigs: gql`
    fragment RewardUnlockedStory_rewardConfig on AnyRewardConfigInterface {
      id
      ... on InGameCurrencyRewardConfig {
        id
        currency
        amount
      }
      ... on CardShardRewardConfig {
        id
        quantity
        configRarity
      }
      ... on MonetaryRewardConfig {
        id
        monetaryAmount: amount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on ConversionCreditRewardConfig {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ...getRewardImage_anyRewardConfigInterface
      ...mergeRewardsConfig_anyRewardConfigInterface
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${getRewardImage.fragments.anyRewardConfigInterface}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardUnlockedStory_rewardConfig>,
};

RewardUnlockedStory.fragments = {
  taskInterface: gql`
    fragment RewardUnlockedStory_taskInterface on TaskInterface {
      id
      probabilisticBundles {
        id
        ...RewardBoxDialogWithOnboarding_probabilisticBundle
      }
      rewardConfigs {
        id
        ...RewardUnlockedStory_rewardConfig
      }
      alternativeRewardConfigs {
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
    }
    ${fragments.rewardConfigs}
    ${RewardBoxDialogWithOnboarding.fragments.probabilisticBundle}
  ` as TypedDocumentNode<RewardUnlockedStory_taskInterface>,
};
